import * as React from "react";
import {clsx} from "clsx";
import {formatDuration, parseDuration} from "@/shared/utils/datefunctions";
import {useDraftInput} from "@/shared/ui/primitives/useDraftInput";

interface DurationInputProps {
	id?: string;
	name?: string;
	/** Duration in milliseconds. */
	value: number;
	/** Fired on blur with the parsed duration in milliseconds, only when the displayed duration changed. */
	onValueChange: (value: number) => void;
	className?: string;
	ariaLabel?: string;
	disabled?: boolean;
}

/**
 * Text-based duration input (per `globals.durationFormat`) with local draft state and blur-time
 * normalization; see {@link useDraftInput} for the draft/commit semantics.
 */
export function DurationInput({
	id,
	name,
	value,
	onValueChange,
	className,
	ariaLabel,
	disabled = false
}: Readonly<DurationInputProps>) {
	const draftInputProps = useDraftInput({
		value,
		format: duration => formatDuration(duration),
		parse: draft => parseDuration(draft) * 60 * 1000,
		onValueChange
	});

	return (
		<input
			id={id}
			type="text"
			name={name}
			className={clsx("form-control", className)}
			aria-label={ariaLabel}
			disabled={disabled}
			{...draftInputProps}
		/>
	);
}

export type {DurationInputProps};
